import { faGavel } from '@fortawesome/pro-regular-svg-icons/faGavel';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { WILSON_NESBITT_LINK } from '@propertypal/shared/src/constants/externalLinks';
import { KEY_INFO_TOOLTIPS } from '@propertypal/shared/src/constants/keyInfo';
import useInView from '@propertypal/shared/src/hooks/useInView';
import { MortgageCalcData } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import CoOwnershipLogo from '@propertypal/shared/src/resources/logos/co-ownership-logo.svg?url';
import WilsonNesbittLogo from '@propertypal/shared/src/resources/logos/wilson-nesbitt-logo.jpg';
import { trackGaEvent, trackGaLink } from '@propertypal/shared/src/services/analytics';
import { getItem } from '@propertypal/shared/src/services/local.storage';
import { GREY_300 } from '@propertypal/shared/src/styles/theme';
import { BooleanKey } from '@propertypal/shared/src/types/legal-fees';
import { Property, KeyInfo } from '@propertypal/shared/src/types/property';
import boolCheck from '@propertypal/shared/src/utils/bool-check';
import floatToCurrency, { parseNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import showBroadbandChecker from '@propertypal/shared/src/utils/property/broadband';
import {
  getDefaultDeposit,
  getDefaultTerm,
} from '@propertypal/shared/src/utils/property/getMortgageCalculatorDefaults';
import {
  groupFinancialInfo,
  groupOverview,
  groupDetailedFeatures,
  ICON_MAP,
  STAMP_DUTY_TEXT_MAP,
  getStyleIcon,
  groupAuctionDetails,
} from '@propertypal/shared/src/utils/property/groupKeyInfo';
import { capitalize } from '@propertypal/shared/src/utils/text';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import Loader from 'react-spinners/ClipLoader';
import { useTheme } from 'styled-components';
import { openTooltip } from '../notifications/Tooltip';
import EnergyRatingIcon from '../property-box/EnergyRatingIcon';
import InfoIcon from '../schools/InfoIcon';
import {
  BottomRow,
  Content,
  CoownershipText,
  KeyInfoGrid,
  KeyInfoItem,
  KeyInfoLegalFees,
  KeyInfoLoader,
  KeyInfoText,
  KeyInfoTitle,
  LegalLogo,
  SubText,
  Title,
  KeyInfoFibrus,
  FibrusLogo,
} from './KeyInfoList.style';

interface Props {
  buyingStatus: string | null;
  ukResident: '' | BooleanKey | null;
  property: Property;
  onClick: (info: KeyInfo) => void;
  mortgageCalculator: MortgageCalcData;
  showLegalFees?: boolean;
  small?: boolean;
  disableCoOwnership?: boolean;
}

const MORTGAGE_INFO: KeyInfo = {
  key: 'TYPICAL_MORTGAGE',
  name: 'Mortgage',
  text: '',
  textHtml: '',
  group: '',
};

const BROADBAND_INFO: KeyInfo = {
  key: 'BROADBAND',
  name: 'Broadband',
  text: '',
  textHtml: '',
  group: '',
};

const ICON_STYLE = {
  fontSize: 20,
  width: 25,
  minWidth: 25,
  marginTop: 2,
};

const TitleInfoIcon: React.FC<{ txt: string }> = ({ txt }) => (
  <InfoIcon onMouseEnter={(e) => openTooltip(txt, e)} icon={faInfoCircle} fontSize={16} testID="keyInfoIcon" />
);

const renderText = (
  info: KeyInfo,
  property: Property,
  onClick: Props['onClick'],
  buyingStatus: string | null,
  small?: boolean,
  ukResident?: Props['ukResident'],
) => {
  const tooltip = KEY_INFO_TOOLTIPS[info.key];

  if (info.key === 'EPC_RATING' || info.key === 'EPC_FILES' || info.key === 'EPC_RATING_CHART') {
    return (
      <KeyInfoItem key={info.key}>
        <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />

        <div>
          <KeyInfoTitle>Energy Rating</KeyInfoTitle>
          <KeyInfoText>
            <button data-testid="epcText" onClick={() => onClick(info)} type="button">
              {info.text}
            </button>
          </KeyInfoText>
        </div>
      </KeyInfoItem>
    );
  }

  if (info.key === 'BER') {
    return (
      <KeyInfoItem key={info.key}>
        <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />
        <div>
          <KeyInfoTitle>Energy Rating</KeyInfoTitle>
          <EnergyRatingIcon property={property} />
        </div>
      </KeyInfoItem>
    );
  }

  if (info.key === 'STAMP_DUTY_COMPLEX' && info.buyerTypeCosts) {
    return (
      <KeyInfoItem key={info.key}>
        <FontAwesomeIcon icon={ICON_MAP[info.key]} style={ICON_STYLE} />

        {!!buyingStatus && (
          <div>
            <KeyInfoTitle>
              <span>Stamp Duty</span>
              <TitleInfoIcon txt={tooltip?.content} />
            </KeyInfoTitle>
            <KeyInfoText data-testid="stampDutyText">
              {info.buyerTypeCosts[`${buyingStatus}${!ukResident || ukResident === 'FALSE' ? '_NON_UK' : ''}`]}*²
            </KeyInfoText>
            <SubText $small={!!small} data-testid="stampDutySmallPrint">
              (Based on being a {STAMP_DUTY_TEXT_MAP[buyingStatus]}){' '}
              <button onClick={() => onClick(info)} type="button">
                Change
              </button>
            </SubText>
          </div>
        )}

        {!buyingStatus && (
          <div>
            <KeyInfoTitle>
              <span>Stamp Duty</span>
              <TitleInfoIcon txt={tooltip?.content} />
            </KeyInfoTitle>
            <KeyInfoText>
              <button data-testid="stampDutyText" onClick={() => onClick(info)} type="button">
                Calculate
              </button>
            </KeyInfoText>
          </div>
        )}
      </KeyInfoItem>
    );
  }

  if (info.key === 'STAMP_DUTY_SIMPLE') {
    return (
      <KeyInfoItem key={info.key} data-testid="stampDutySimple">
        <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />

        <div>
          <KeyInfoTitle>Stamp Duty</KeyInfoTitle>
          <KeyInfoText>{info.text}*²</KeyInfoText>
        </div>
      </KeyInfoItem>
    );
  }

  if (info.key === 'BROADBAND') {
    return (
      <KeyInfoItem key={info.key} data-testid="broadbandSpeed">
        <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />

        <div>
          <KeyInfoTitle>Broadband Speed Checker</KeyInfoTitle>
          <KeyInfoText>
            <button data-testid="broadbandSpeedButton" onClick={() => onClick(info)} type="button">
              {property?.broadbandSpeed?.fibrusAvailable
                ? 'Fibrus Available'
                : property.broadbandSpeed?.maxUfbbPredictedDown
                ? 'Ultrafast'
                : property.broadbandSpeed?.maxSfbbPredictedDown
                ? 'Superfast'
                : 'Standard'}
              {!property?.broadbandSpeed?.fibrusAvailable && ' Broadband'}
            </button>
            *³
          </KeyInfoText>
          <KeyInfoFibrus data-testid="broadband-fibrus" onClick={() => onClick(info)}>
            <span>In partnership with</span>
            <FibrusLogo />
          </KeyInfoFibrus>
        </div>
      </KeyInfoItem>
    );
  }

  if (info.key === 'STATUS') {
    return (
      <KeyInfoItem key={info.key}>
        <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />

        <div>
          <KeyInfoTitle>{info.name}</KeyInfoTitle>
          <KeyInfoText>
            {['Let agreed', 'Sale agreed', 'Sold', 'Let'].includes(info.text) ? (
              <b>{capitalize(info.text)}</b>
            ) : (
              capitalize(info.text)
            )}
          </KeyInfoText>
        </div>
      </KeyInfoItem>
    );
  }

  if (info.key === 'AUCTION_DATE') {
    return (
      <KeyInfoItem key={info.key}>
        <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />

        <div>
          <KeyInfoTitle>Auction Date</KeyInfoTitle>
          <KeyInfoText>{info.text === 'finished' ? <b>Finished</b> : info.text}</KeyInfoText>
        </div>
      </KeyInfoItem>
    );
  }

  if (info.key === 'STYLE' || info.key === 'LAND_TYPE') {
    return (
      <KeyInfoItem key={info.key}>
        <FontAwesomeIcon icon={getStyleIcon(info.text)} color={GREY_300} style={ICON_STYLE} />

        <div>
          <KeyInfoTitle>{info.name}</KeyInfoTitle>
          <KeyInfoText>{info.text}</KeyInfoText>
        </div>
      </KeyInfoItem>
    );
  }

  return (
    <KeyInfoItem key={info.key}>
      {info.key in ICON_MAP && <FontAwesomeIcon icon={ICON_MAP[info.key]} color={GREY_300} style={ICON_STYLE} />}
      <div>
        <KeyInfoTitle>
          <span>{info.name}</span>
          {tooltip && tooltip.type !== 'text' && <TitleInfoIcon txt={tooltip.content} />}
        </KeyInfoTitle>
        <KeyInfoText>
          <span>
            {info.text}
            {info.key === 'RATES' && <>*¹</>}
            {info.key === 'YEAR_BUILT' && <>*⁴</>}
          </span>
          {tooltip && tooltip.type === 'text' && <TitleInfoIcon txt={tooltip.content} />}
        </KeyInfoText>
      </div>
    </KeyInfoItem>
  );
};

const KeyInfoList: FunctionComponent<Props> = ({
  property,
  mortgageCalculator,
  showLegalFees,
  onClick,
  buyingStatus,
  ukResident,
  small,
  disableCoOwnership,
}) => {
  const isFeaturedDevelopment = !!property.featuredDevelopmentContent;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const overview = groupOverview(property.keyInfo);
  const auctionDetails = groupAuctionDetails(property.keyInfo);
  const detailedFeatures = groupDetailedFeatures(property.keyInfo);
  const financialInfo = groupFinancialInfo(property.keyInfo);
  const coOwnership = property.keyInfo?.find((info) => info.key === 'COOWNERSHIP');
  const { data, error, errorStatus, depositPercentage, hasMortgageCalculator, getMortgageResult } = mortgageCalculator;
  const showFinancial = financialInfo?.length || hasMortgageCalculator;
  const coOwnershipRef = useRef<HTMLParagraphElement>(null);
  const legalRef = useRef<HTMLDivElement>(null);
  const broadbandChecker = showBroadbandChecker(property);

  const fetchMortgageResult = () => {
    if (!data && !error && hasMortgageCalculator) {
      const defaultTerm = getItem<string>('defaultTerm');
      const defaultDeposit = getItem<string>('defaultDeposit');

      getMortgageResult({
        propertyValue: property.price?.price,
        paymentTerm: defaultTerm ? parseNumber(defaultTerm) : getDefaultTerm(property),
        deposit: defaultDeposit ? parseNumber(defaultDeposit) : getDefaultDeposit(property),
      });
    }
  };

  useInView(containerRef, { threshold: 0.5, rootMargin: '0px 0px 600px' }, fetchMortgageResult);

  useInView(containerRef, { threshold: 0.5 }, () => {
    trackGaEvent('key_info_fibrus_impression', {
      page: property?.development ? 'DDP' : 'PDP',
      fibrus: boolCheck(property?.broadbandSpeed?.fibrusAvailable),
    });
  });

  useInView(coOwnershipRef, { threshold: 0.5 }, () => {
    trackGaEvent('coownership_banner_impression', {
      page: 'PDP',
      location: property.coOwnershipOnly ? 'LDP' : 'Property Details Page',
    });
  });

  useInView(legalRef, { threshold: 0.5 }, () => {
    trackGaEvent('legal_fees_ad_impression', {
      page: 'PDP',
      location: 'Key Info',
    });
  });

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Anchor tag was clicked inside co-ownership banner
      if ((e?.target as HTMLElement)?.tagName === 'A')
        trackGaEvent('coownership_banner_click', {
          page: 'PDP',
          location: property.coOwnershipOnly ? 'LDP' : 'Property Details Page',
        });
    };
    const node = coOwnershipRef.current;
    if (node) node.addEventListener('click', handleClick);
    return () => {
      if (node) node.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <>
      {!!auctionDetails?.length && (
        <Content isFeaturedDevelopment={isFeaturedDevelopment}>
          <Title $small={!!small}>Auction Details</Title>

          <KeyInfoGrid $small={!!small}>
            {auctionDetails?.map((info) => renderText(info, property, onClick, buyingStatus, small))}
          </KeyInfoGrid>
        </Content>
      )}

      <Content ref={containerRef} isFeaturedDevelopment={isFeaturedDevelopment}>
        <Title $small={!!small}>Property Overview</Title>

        <KeyInfoGrid $small={!!small}>
          {overview?.map((info) => renderText(info, property, onClick, buyingStatus, small))}
        </KeyInfoGrid>
      </Content>

      {(!!detailedFeatures?.length || broadbandChecker) && (
        <Content isFeaturedDevelopment={isFeaturedDevelopment}>
          <Title $small={!!small}>Property Features</Title>

          <KeyInfoGrid $small={!!small}>
            {detailedFeatures?.map((info) => renderText(info, property, onClick, buyingStatus, small))}

            {broadbandChecker && (
              <KeyInfoItem data-testid="broadbandSpeed">
                <FontAwesomeIcon icon={ICON_MAP.BROADBAND} color={GREY_300} style={ICON_STYLE} />

                <div>
                  <KeyInfoTitle>Broadband Speed Checker</KeyInfoTitle>
                  <KeyInfoText>
                    <button onClick={() => onClick(BROADBAND_INFO)} type="button">
                      Check Postcode
                    </button>
                  </KeyInfoText>
                  <KeyInfoFibrus data-testid="broadband-fibrus" onClick={() => onClick(BROADBAND_INFO)}>
                    <span>In partnership with</span>
                    <FibrusLogo />
                  </KeyInfoFibrus>
                </div>
              </KeyInfoItem>
            )}
          </KeyInfoGrid>
        </Content>
      )}

      {showFinancial && (
        <Content isFeaturedDevelopment={isFeaturedDevelopment}>
          <Title $small={!!small}>Property Financials</Title>

          <KeyInfoGrid $small={!!small} data-testid="key-info-grid">
            {financialInfo?.map((info) => renderText(info, property, onClick, buyingStatus, small, ukResident))}

            {hasMortgageCalculator && (
              <KeyInfoItem>
                <FontAwesomeIcon icon={faMoneyBill} color={theme.textDark} style={ICON_STYLE} />

                <div>
                  <KeyInfoTitle>Typical Mortgage</KeyInfoTitle>

                  {!data && !errorStatus && !error && (
                    <KeyInfoLoader>
                      <Loader color={theme.backgroundLight} size={28} />
                    </KeyInfoLoader>
                  )}

                  {(errorStatus || error) && (
                    <>
                      <KeyInfoText data-testid="no-mortgage-result">No mortgage results</KeyInfoText>

                      <SubText $small={!!small}>
                        Try changing your{' '}
                        <button onClick={() => onClick(MORTGAGE_INFO)} type="button">
                          criteria
                        </button>{' '}
                        below
                      </SubText>
                    </>
                  )}

                  {!(errorStatus || error) && !!data?.product?.initialMonthlyPayment && (
                    <>
                      <KeyInfoText data-testid="typical-mortgage-text">
                        <button onClick={() => onClick(MORTGAGE_INFO)} type="button">{`${floatToCurrency(
                          data.product.initialMonthlyPayment,
                        )} per month`}</button>
                      </KeyInfoText>

                      <SubText $small={!!small} data-testid="typical-mortgage-subtext">
                        {`${floatToCurrency(data.deposit, {
                          precision: 0,
                        })} (${depositPercentage}%) deposit (${data.term} year term)`}
                      </SubText>
                    </>
                  )}
                </div>
              </KeyInfoItem>
            )}

            {showLegalFees && (
              <KeyInfoItem ref={legalRef}>
                <FontAwesomeIcon icon={faGavel} style={ICON_STYLE} />
                <div>
                  <KeyInfoTitle>Legal Fees Quotation</KeyInfoTitle>
                  <KeyInfoText>
                    <a
                      href={`${WILSON_NESBITT_LINK}&utm_content=text_link`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="legal-fees-btn"
                      onClick={trackGaLink('legal_fees_click', 'Key Info - Hyperlink', 'PDP')}
                    >
                      Get a quote for your legal costs
                    </a>
                  </KeyInfoText>
                  <KeyInfoLegalFees
                    href={`${WILSON_NESBITT_LINK}&utm_content=text_link`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={trackGaLink('legal_fees_click', 'Key Info - Logo', 'PDP')}
                  >
                    <span>In partnership with</span>
                    <LegalLogo src={WilsonNesbittLogo.src} alt="Legal Logo" />
                  </KeyInfoLegalFees>
                </div>
              </KeyInfoItem>
            )}
          </KeyInfoGrid>
        </Content>
      )}

      {coOwnership && !disableCoOwnership && (
        <BottomRow data-testid="co-ownership-banner">
          <img src={CoOwnershipLogo.src} className="co-ownership-logo" alt="Co-Ownership" />
          <CoownershipText ref={coOwnershipRef}>
            This property may be suitable for Co-Ownership. Find out how they could{' '}
            <a href="https://bit.ly/46fsa1q" target="_blank" rel="noreferrer">
              help you
            </a>{' '}
            become a homeowner.
          </CoownershipText>
        </BottomRow>
      )}
    </>
  );
};

export default KeyInfoList;
